import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Slider from "react-slick";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

// Helper function to build full image URL from relative path
const buildImageUrl = (imagePath) => {
  if (!imagePath) return '';
  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If relative path, prepend base URL if available
  const imageBase = import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || '';
  if (imageBase && !imagePath.startsWith('/')) {
    return `${imageBase}/${imagePath}`;
  } else if (imageBase) {
    return `${imageBase}${imagePath}`;
  }
  return imagePath;
};

// Helper to get impact data from API
const getImpactData = (donationData) => {
  if (donationData?.impactDescriptions && Array.isArray(donationData.impactDescriptions) && donationData.impactDescriptions.length > 0) {
    return donationData.impactDescriptions
      .filter(impact => impact.status === 1)
      .map((impact, idx) => {
        // Extract image URL from images array
        let imageUrl = null;
        if (impact.images && Array.isArray(impact.images) && impact.images.length > 0) {
          const firstImage = impact.images[0];
          if (typeof firstImage === 'string') {
            // Direct URL string
            imageUrl = buildImageUrl(firstImage);
          } else if (firstImage && typeof firstImage === 'object') {
            // Handle object with url property (e.g., { id: 123, url: "uploads/..." })
            if (firstImage.url && typeof firstImage.url === 'string') {
              imageUrl = buildImageUrl(firstImage.url);
            }
          }
        } else {
          // Debug: Log when images array is empty or missing
          console.log(`Impact ${idx + 1} (ID: ${impact.id}) has no images. Images array:`, impact.images);
        }
        
        return {
          id: impact.id || `impact-${idx}`,
          title: impact.title || `Impact ${idx + 1}`,
          description: impact.description || '',
          image: imageUrl,
        };
      });
  }
  // Return empty array if no API data
  return [];
};

const Arrow = ({ onClick, direction, disabled }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      width: 48,
      height: 48,
      borderRadius: "50%",
      background: disabled 
        ? "#b9b9b8"  // Light grey/white when disabled
        : "#1D1D1B", // Black when active
      color: disabled 
        ? "#1D1D1B"  // Dark arrow when disabled (on light background)
        : "#fff",    // White arrow when active (on black background)
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "not-allowed" : "pointer",
      ml: direction === "right" ? 2 : 0,
      boxShadow: 1,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {direction === "left" ? (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
        <path fillRule="evenodd" clipRule="evenodd" d="M7.05505 0.331356C6.83459 0.110902 6.47721 0.110902 6.25675 0.331356L0.987921 5.60013C0.882018 5.70596 0.822555 5.84957 0.822555 5.99928C0.822555 6.14899 0.882018 6.2926 0.987921 6.39851L6.25675 11.6673C6.47721 11.8878 6.83459 11.8878 7.05505 11.6673C7.27551 11.4469 7.27551 11.0894 7.05505 10.869L2.74997 6.5638L14.9355 6.5638C15.2473 6.5638 15.5 6.31104 15.5 5.99928C15.5 5.68752 15.2473 5.43476 14.9355 5.43476L2.74997 5.43476L7.05505 1.1297C7.27551 0.909249 7.27551 0.55182 7.05505 0.331356Z" fill="currentColor"/>
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
        <path fillRule="evenodd" clipRule="evenodd" d="M8.94495 0.331356C9.16541 0.110902 9.52279 0.110902 9.74325 0.331356L15.0121 5.60013C15.118 5.70596 15.1774 5.84957 15.1774 5.99928C15.1774 6.14899 15.118 6.2926 15.0121 6.39851L9.74325 11.6673C9.52279 11.8878 9.16541 11.8878 8.94495 11.6673C8.72449 11.4469 8.72449 11.0894 8.94495 10.869L13.25 6.5638L1.06452 6.5638C0.752745 6.5638 0.5 6.31104 0.5 5.99928C0.5 5.68752 0.752745 5.43476 1.06452 5.43476L13.25 5.43476L8.94495 1.1297C8.72449 0.909249 8.72449 0.55182 8.94495 0.331356Z" fill="currentColor"/>
      </svg>
    )}
  </Box>
);

const CMS_SECTION_PAGE = "Monthly donation";
const CMS_SECTION_NAME = "Impact Of Your Donation";

const DonationImpactSlider = ({ donationData }) => {
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const sliderRef = React.useRef();
  const [currentSlide, setCurrentSlide] = useState(0);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const isSmallScreen = useMediaQuery('(max-width: 899px)');
  const isMediumScreen = useMediaQuery('(min-width: 900px) and (max-width: 1200px)');

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const impactSectionCms = useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    return data.find(
      (s) => s?.page === CMS_SECTION_PAGE && s?.section === CMS_SECTION_NAME
    ) || null;
  }, [cmsHome]);

  const impactData = getImpactData(donationData);
  const dataLength = impactData.length;

  // Fixed slide widths (like Your Impact): mobile 190x250, desktop 403x440
  const slideCardWidth = isMobile ? 190 : 403;
  const slidePadding = isMobile ? 1 : 3;
  const totalSlideWidth = slideCardWidth + slidePadding;

  // Approximate how many slides are visible based on breakpoint (for disabling Next properly)
  const visibleSlides = (() => {
    if (isMobile) return 1;
    if (isSmallScreen) return 2;
    if (isMediumScreen) return 2;
    return 3; // large desktop
  })();

  const effectiveVisible = Math.min(visibleSlides, dataLength);
  const lastStartIndex = Math.max(0, dataLength - effectiveVisible);

  const isPrevDisabled = currentSlide === 0;
  const isNextDisabled = dataLength <= effectiveVisible || currentSlide >= lastStartIndex;

  useEffect(() => {
    setCurrentSlide(0);
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(0);
    }
  }, [dataLength]);

  // Keep after all hooks to avoid "Rendered fewer hooks than expected"
  if (dataLength === 0) return null;

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    variableWidth: true,
    slidesToScroll: 1,
    arrows: false,
    autoplay: false,
    centerMode: false,
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    draggable: true,
    beforeChange: (current, next) => {
      setCurrentSlide(next);
    },
  };

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        mx: "auto",
        mt: isMobile ? "20px" : 6,
        background: "#F2F3F4",
        borderRadius: isMobile ? "0" : "32px",
        p: isMobile ? "20px 16px" : isSmallScreen ? "24px" : isMediumScreen ? "30px" : "50px 40px",
        position: "relative",
        overflow: "hidden", // Changed to hidden to prevent overflow
      }}
    >
      {/* Heading with arrows on right */}
      <Box
        sx={{
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          mb: isMobile ? "20px" : "40px",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              color: "#131313",
              fontWeight: 400,
              letterSpacing: "0.1em",
              mb: 1,
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                color: "#A9C74E",
                fontSize: "20px",
                lineHeight: 1,
              }}
            >
              ●
            </Box>
            What You Done
          </Typography>
          <Typography
            sx={{
              fontSize: isMobile ? "18px" : isSmallScreen ? "24px" : isMediumScreen ? "30px" : "36px",
              fontWeight: 700,
              color: "#1D1D1B",
              mb: "10px",
              lineHeight: 1.2,
            }}
          >
            Impact Of Your Donation
          </Typography>
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : isSmallScreen ? "14px" : "16px",
              color: "#7D7D7D",
              fontWeight: 400,
              maxWidth: isMobile ? "100%" : isSmallScreen ? "350px" : "100%",
              lineHeight: 1.5,
            }}
          >
            {donationData?.monthly_header_description1 || impactSectionCms?.description || ""}
          </Typography>
        </Box>
        
        {/* Arrows - Desktop */}
        <Box sx={{ 
          display: isMobile ? "none" : "flex", 
          alignItems: "center", 
          ml: 2,
          flexShrink: 0 
        }}>
          <Arrow 
            direction="left" 
            onClick={() => {
              if (!isPrevDisabled && sliderRef.current) {
                sliderRef.current.slickPrev();
              }
            }} 
            disabled={isPrevDisabled}
          />
          <Arrow 
            direction="right" 
            onClick={() => {
              if (!isNextDisabled && sliderRef.current) {
                sliderRef.current.slickNext();
              }
            }} 
            disabled={isNextDisabled}
          />
        </Box>
      </Box>

      {/* Slider */}
      <Box sx={{ position: "relative" }}>
        <Slider ref={sliderRef} {...sliderSettings}>
          {impactData.map((item) => (
            <Box
              key={item.id}
              sx={{
                width: totalSlideWidth,
                px: slidePadding / 2,
                outline: "none",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "16px",
                  overflow: "hidden",
                  background: "#fff",
                  width: slideCardWidth,
                  height: isMobile ? "250px" : "440px",
                }}
              >
                {item.image ? (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      // Hide broken images
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f5f5f5",
                      color: "#999",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px" }}>No Image</Typography>
                  </Box>
                )}
                
                {/* Content Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    right: "10px",
                    p: isMobile ? "12px" : "17px",
                    backdropFilter: "blur(25px)",
                    background: "rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    color: "#fff",
                    maxHeight: isMobile ? "90px" : "138px",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ 
                      fontWeight: 700, 
                      mb: "4px", 
                      fontSize: isMobile ? "12px" : "18px",
                      lineHeight: 1.2,
                      color: "#fff"
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ 
                    fontSize: isMobile ? "12px" : "14px", 
                    color: "rgba(255, 255, 255, 0.85)",
                    lineHeight: 1.4,
                    display: "-webkit-box",
                    WebkitLineClamp: isMobile ? 3 : 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Slider>
        
        {/* Arrows - Mobile */}
        <Box sx={{ 
          display: isMobile ? "flex" : "none", 
          justifyContent: "center", 
          alignItems: "center",
          gap: 2,
          mt: 3 
        }}>
          <Arrow 
            direction="left" 
            onClick={() => {
              if (!isPrevDisabled && sliderRef.current) {
                sliderRef.current.slickPrev();
              }
            }} 
            disabled={isPrevDisabled}
          />
          <Arrow 
            direction="right" 
            onClick={() => {
              if (!isNextDisabled && sliderRef.current) {
                sliderRef.current.slickNext();
              }
            }} 
            disabled={isNextDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DonationImpactSlider;